import { SchemaMarkup, BreadcrumbItem } from '@/types/seo';
import { seoConfig } from '@/config/seo';

interface SchemaGeneratorProps {
  type: 'WebPage' | 'Article' | 'Service' | 'Organization' | 'LocalBusiness' | 'FAQPage';
  title: string;
  description: string;
  url: string;
  image?: string;
  breadcrumbs?: BreadcrumbItem[];
  author?: string;
  publishDate?: string;
  modifiedDate?: string;
  services?: Array<{
    name: string;
    description: string;
    url?: string;
  }>;
  faqs?: Array<{
    question: string;
    answer: string;
  }>;
}

export const generateSchemaMarkup = (props: SchemaGeneratorProps): SchemaMarkup[] => {
  const schemas: SchemaMarkup[] = [];

  // Always include Organization schema
  schemas.push(generateOrganizationSchema());

  // Always include WebSite schema
  schemas.push(generateWebSiteSchema());

  // Add specific schema based on type
  switch (props.type) {
    case 'WebPage':
      schemas.push(generateWebPageSchema(props));
      break;
    case 'Article':
      schemas.push(generateArticleSchema(props));
      break;
    case 'Service':
      schemas.push(generateServiceSchema(props));
      break;
    case 'LocalBusiness':
      schemas.push(generateLocalBusinessSchema(props));
      break;
    case 'FAQPage':
      schemas.push(generateFAQPageSchema(props));
      break;
  }

  // Add breadcrumb schema if breadcrumbs exist
  if (props.breadcrumbs && props.breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(props.breadcrumbs));
  }

  return schemas;
};

const generateOrganizationSchema = (): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: seoConfig.organization.name,
  alternateName: seoConfig.organization.alternateName,
  url: seoConfig.organization.url,
  logo: seoConfig.organization.logo,
  description: seoConfig.organization.description,
  email: seoConfig.organization.email,
  telephone: seoConfig.organization.telephone,
  address: {
    '@type': 'PostalAddress',
    addressLocality: seoConfig.organization.address.addressLocality,
    addressRegion: seoConfig.organization.address.addressRegion,
    addressCountry: seoConfig.organization.address.addressCountry,
  },
  sameAs: seoConfig.organization.sameAs,
  foundingDate: seoConfig.organization.foundingDate,
  numberOfEmployees: {
    '@type': 'QuantitativeValue',
    minValue: seoConfig.organization.numberOfEmployees?.minValue,
    maxValue: seoConfig.organization.numberOfEmployees?.maxValue,
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5',
    reviewCount: '127',
    bestRating: '5',
    worstRating: '1',
  },
  offers: [
    {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'AI Solutions & Machine Learning',
        description: 'Enterprise AI agents, RAG systems, and machine learning implementations with 650% ROI',
      },
    },
    {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'NetSuite ERP Development',
        description: 'Custom NetSuite implementations and SuiteScript development for enterprise businesses',
      },
    },
    {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Shopify Plus Development',
        description: 'Scalable e-commerce solutions on Shopify Plus platform with AI-powered features',
      },
    },
    {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Celigo Integration Platform',
        description: 'Enterprise integration solutions connecting 100+ business systems',
      },
    },
    {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'PowerBI Analytics',
        description: 'Real-time business intelligence dashboards with predictive analytics',
      },
    },
  ],
});

const generateWebSiteSchema = (): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: seoConfig.site.name,
  url: seoConfig.site.url,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${seoConfig.site.url}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

const generateWebPageSchema = (props: SchemaGeneratorProps): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: props.title,
  description: props.description,
  url: props.url,
  image: props.image,
  isPartOf: {
    '@type': 'WebSite',
    name: seoConfig.site.name,
    url: seoConfig.site.url,
  },
  about: {
    '@type': 'Organization',
    name: seoConfig.organization.name,
  },
  lastReviewed: new Date().toISOString(),
  reviewedBy: {
    '@type': 'Organization',
    name: seoConfig.organization.name,
  },
});

const generateArticleSchema = (props: SchemaGeneratorProps): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: props.title,
  description: props.description,
  url: props.url,
  image: props.image,
  author: {
    '@type': 'Person',
    name: props.author || seoConfig.organization.name,
  },
  publisher: {
    '@type': 'Organization',
    name: seoConfig.organization.name,
    logo: {
      '@type': 'ImageObject',
      url: seoConfig.organization.logo,
    },
  },
  datePublished: props.publishDate,
  dateModified: props.modifiedDate || props.publishDate,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': props.url,
  },
});

const generateServiceSchema = (props: SchemaGeneratorProps): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: props.title,
  description: props.description,
  url: props.url,
  image: props.image,
  provider: {
    '@type': 'Organization',
    name: seoConfig.organization.name,
    url: seoConfig.organization.url,
  },
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  serviceType: 'Technology Solutions',
  category: 'AI and Enterprise Software Development',
});

const generateLocalBusinessSchema = (props: SchemaGeneratorProps): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: seoConfig.organization.name,
  description: props.description,
  url: props.url,
  image: props.image,
  telephone: seoConfig.organization.telephone,
  email: seoConfig.organization.email,
  address: {
    '@type': 'PostalAddress',
    addressLocality: seoConfig.organization.address.addressLocality,
    addressRegion: seoConfig.organization.address.addressRegion,
    addressCountry: seoConfig.organization.address.addressCountry,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '33.9533',
    longitude: '-117.3962',
  },
  openingHours: 'Mo-Fr 09:00-17:00',
  priceRange: '$$$',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5',
    reviewCount: '127',
  },
});

const generateFAQPageSchema = (props: SchemaGeneratorProps): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: props.faqs?.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })) || [],
});

const generateBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]): SchemaMarkup => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: crumb.url,
  })),
});